import { ExternalServiceId, Uuid } from '@edgebox/data-definition-kit';
import { IRuntimeMetadataFacetDiff, IRuntimeMetadataFacetGroup } from '@edgebox/sync-core-data-definitions';

export interface IRegisterSite {
  type: 'register-site';
}

export interface ISaveFlow {
  type: 'save-flow';
  data: any;
}

export interface IMigrate {
  type: 'migrate';
}

export interface IMigrationExportPools {
  type: 'migration-export-pools';
  machineNames: ExternalServiceId[];
}

export interface IMigrationExportFlows {
  type: 'migration-export-flows';
  machineNames: ExternalServiceId[];
}

export interface IMigrationSkipFlowsTest {
  type: 'migration-skip-flows-test';
  machineNames: ExternalServiceId[];
}

export interface IMigrationSkipFlowsPush {
  type: 'migration-skip-flows-push';
  machineNames: ExternalServiceId[];
}

export interface IMigrationSkipFlowsPull {
  type: 'migration-skip-flows-pull';
  machineNames: ExternalServiceId[];
}

export interface IMigrationSwitch {
  type: 'migration-switch';
}

export interface IUpdateQuery {
  type: 'update-query';
  query: any;
}

export interface IUpdatePath {
  type: 'update-path';
  path: string;
}

export interface IRenderOptimizeDiff {
  type: 'render-optimize-diff';
  sourceHtml: string | null;
  targetHtml: string | null;
  snapshotHtml?: string | null;
  changeGroups?: Array<{
    id: string;
    changeIds: string[];
    sourceChangeIds?: string[];
    canApprove?: boolean;
  }>;
  metadataChanges?: IRuntimeMetadataFacetDiff[];
  metadataChangeGroups?: IRuntimeMetadataFacetGroup[];
  /**
   * Set when the site wrote the draft itself: the host shows the comparison read only and offers its own
   * publishing instead of approving changes here. Absent for a draft this service generated; a host
   * that does not read it shows the comparison as it shows any other.
   */
  authoredExternally?: true;
}

export interface IApplyOptimizeChanges {
  type: 'apply-optimize-changes';
  approvedActions: unknown[];
  approvedCount?: number;
  totalChanges?: number;
}

/**
 * Sent while a suggested version of the page is shown for review, and again whenever its figures change, to a host
 * that said it reads it (`optimize-host-reads`): the page's health now and in the suggested version, each as Sync
 * Core stores it (a fraction of 1), so the host can keep them beside the comparison it draws. `suggested` is absent
 * while Sync Core has not measured the suggested version, and `current` while the page has no figure. A host that
 * never said so is sent none: every host released before it, whose dispatcher answers a message it does not know
 * with an error, sees nothing new and shows the comparison as it always did.
 */
export interface IOptimizeHealth {
  type: 'optimize-health';
  current?: number;
  suggested?: number;
}

/**
 * Sent by a host that reads messages of the comparison beyond `render-optimize-diff`, naming them: once it listens,
 * and again with each comparison it is sent, so a frame that loaded after it hears it too. The frame sends such a
 * message only to a host that named it. A frame that does not read it sends what it always sent.
 */
export interface IOptimizeHostReads {
  type: 'optimize-host-reads';
  reads: Array<IOptimizeHealth['type']>;
}

/**
 * Sent by a host once it listens for `render-optimize-diff`. A host whose own scripts run after the frame
 * posted its comparison missed it; the frame answers by posting its latest comparison again, once. It
 * posts nothing when the latest message was a withdrawal, which a host that missed it has nothing to
 * take off for, and nothing when it has posted none yet. A host that never sends it is shown the
 * comparison as posted; a frame that does not read it leaves the host as it is.
 */
export interface IOptimizeHostReady {
  type: 'optimize-host-ready';
}

/**
 * Sent by a host that sizes the frame to its content and scrolls it with its own page, whenever that
 * scroll or the host's size changes: the host's visible area, in the frame's own coordinates. `top` is
 * how far the frame's top edge is above the visible area, in pixels, and 0 while the edge is in view;
 * the frame keeps its navigation at that line. `height` is how far the visible area reaches down from
 * `top`, in pixels: it ends at the visible area's bottom, which may lie past the frame's own end, and
 * it is 0 only while the frame starts below the visible area. What the frame lays over the part in view
 * fits in it. A host that does not send it keeps the area's navigation where the
 * stylesheet puts it: sticking to the top of a fixed-height frame whose body scrolls, or at the top of
 * a frame a CMS page sizes to its content.
 */
export interface IHostViewport {
  type: 'host-viewport';
  top: number;
  height: number;
}

export interface IResolveBrandPresenceOnboardingSite {
  type: 'resolve-brand-presence-onboarding-site';
  domain: string;
  callbackId?: string;
  callback: (result?: IResolveBrandPresenceOnboardingSiteResponse) => void;
}

export interface IResolveBrandPresenceOnboardingSiteResponse {
  site?: {
    id: string;
    uuid: string;
    baseUrl: string;
    name: string;
  };
  error?: string;
}

export interface IListEntity {
  remoteUuid?: Uuid;
  remoteUniqueId?: ExternalServiceId;
  name: string;
  entityTypeNamespaceMachineName: ExternalServiceId;
  entityTypeMachineName: ExternalServiceId;
}
export interface IListEntityResponse {
  items: IListEntity[];
  page: number;
  numberOfPages: number;
  totalNumberOfItems: number;
}
export const siteRequests: { [key: string]: (data: any) => void } = {};
export type ListRequestMode = 'all' | 'pushed' | 'push-failed' | 'pulled';
export interface IListEntities {
  type: 'list-entities';
  page: number;
  itemsPerPage: number;
  mode: ListRequestMode;
  namespaceMachineName: ExternalServiceId;
  machineName: ExternalServiceId;
  flowMachineName?: ExternalServiceId;
  search?: string;
  callbackId?: string;
  callback: (result?: IListEntityResponse) => void;
}
export interface ICountEntities {
  type: 'count-entities';
  mode: ListRequestMode;
  namespaceMachineName: ExternalServiceId;
  machineName: ExternalServiceId;
  flowMachineName?: ExternalServiceId;
  search?: string;
  callbackId?: string;
  callback: (result?: IListEntityResponse) => void;
}
export interface IEntityDetails {
  remoteUuid?: Uuid;
  remoteUniqueId?: ExternalServiceId;
  name: string;
  entityTypeByMachineName: {
    namespaceMachineName: ExternalServiceId;
    machineName: ExternalServiceId;
  };
}
export interface IRetrieveEntity {
  type: 'retrieve-entity';
  namespaceMachineName: ExternalServiceId;
  machineName: ExternalServiceId;
  sharedEntityId: Uuid | ExternalServiceId;
  flowMachineName?: ExternalServiceId;
  callbackId?: string;
  callback: (result?: IEntityDetails) => void;
}

export interface IScrollToTop {
  type: 'scroll-to-top';
}

/**
 * Sent by a Brand Presence screen to a host that scrolls the frame with its own page, that is one that
 * sends `host-viewport`, when the focus moves to an element: the part of the frame to bring into the
 * host's visible area, in the frame's own coordinates. `top` is the element's top less the height of
 * the bars the area pins over the top of the visible area, so the element comes to rest below them;
 * `bottom` is the element's bottom. The host scrolls by the least that shows the part, its top first
 * where the part is taller than the visible area, and not at all where the part is in view. No other
 * host is sent it.
 */
export interface IScrollIntoView {
  type: 'scroll-into-view';
  top: number;
  bottom: number;
}

export interface IModalOpen {
  type: 'modal-open';
}

export interface IModalClose {
  type: 'modal-close';
}

export interface ICountEntity {
  type: 'count';
  entityType: string;
  count: number;
}

export type AnyFrameMessage =
  | IModalOpen
  | IModalClose
  | IScrollToTop
  | IScrollIntoView
  | IRegisterSite
  | ISaveFlow
  | IListEntities
  | ICountEntities
  | ICountEntity
  | IRetrieveEntity
  | IResolveBrandPresenceOnboardingSite
  | IUpdateQuery
  | IUpdatePath
  | IRenderOptimizeDiff
  | IApplyOptimizeChanges
  | IOptimizeHealth
  | IOptimizeHostReads
  | IOptimizeHostReady
  | IMigrate
  | IMigrationExportPools
  | IMigrationExportFlows
  | IMigrationSkipFlowsTest
  | IMigrationSkipFlowsPush
  | IMigrationSkipFlowsPull
  | IMigrationSwitch;

export function sendToParent(message: AnyFrameMessage) {
  if (
    message.type === 'list-entities' ||
    message.type === 'count-entities' ||
    message.type === 'retrieve-entity' ||
    message.type === 'resolve-brand-presence-onboarding-site'
  ) {
    if (!message.callbackId) {
      message.callbackId = `${message.type}-${Date.now()}-${Math.random()}`;
    }
    siteRequests[message.callbackId!] = message.callback;
    delete (message as any).callback;
  }
  (window as any).parentIFrame.sendMessage(message);
}
export function scrollToTop() {
  sendToParent({
    type: 'scroll-to-top',
  });
}
export function scrollIntoView(part: Omit<IScrollIntoView, 'type'>) {
  sendToParent({
    type: 'scroll-into-view',
    top: part.top,
    bottom: part.bottom,
  });
}
export function modalOpen() {
  sendToParent({
    type: 'modal-open',
  });
}
export function modalClose() {
  sendToParent({
    type: 'modal-close',
  });
}
export function listEntities(message: Omit<IListEntities, 'type' | 'callback' | 'callbackId'>) {
  return new Promise<IListEntityResponse>((resolve, reject) => {
    sendToParent({
      type: 'list-entities',
      ...message,
      callback: (result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Request failed.'));
        }
      },
    });
  });
}
export function countEntities(message: Omit<ICountEntities, 'type' | 'callback' | 'callbackId'>) {
  return new Promise<number>((resolve, reject) => {
    sendToParent({
      type: 'count-entities',
      ...message,
      callback: (result) => {
        if (result) {
          resolve(result.totalNumberOfItems);
        } else {
          reject(new Error('Request failed.'));
        }
      },
    });
  });
}
export function retrieveEntity(message: Omit<IRetrieveEntity, 'type' | 'callback' | 'callbackId'>) {
  return new Promise<IEntityDetails>((resolve, reject) => {
    sendToParent({
      type: 'retrieve-entity',
      ...message,
      callback: (result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Request failed.'));
        }
      },
    });
  });
}

export function resolveBrandPresenceOnboardingSite(domain: string) {
  return new Promise<NonNullable<IResolveBrandPresenceOnboardingSiteResponse['site']>>((resolve, reject) => {
    sendToParent({
      type: 'resolve-brand-presence-onboarding-site',
      domain,
      callback: (result) => {
        if (!result) {
          reject(new Error('Request failed.'));
          return;
        }

        if (result.error) {
          reject(new Error(result.error));
          return;
        }

        if (!result.site) {
          reject(new Error('No site returned by parent app.'));
          return;
        }

        resolve(result.site);
      },
    });
  });
}
